import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayingCard } from "../PlayingCard";

const W = 44;
const H = 64;

// Returns keyframes + transition config based on animation type
function getProfile(type, dx, dy) {
  const dir = dx >= 0 ? 1 : -1;

  switch (type) {
    case "draw":
      // Card glides smoothly out of deck toward hand — no arc, slight pop
      return {
        keyframes: {
          x:       [0, dx * 0.52, dx],
          y:       [0, dy * 0.52, dy],
          rotate:  [-8, 2, 0],
          scale:   [0.86, 1.06, 0.94],
          opacity: [0.82, 1, 1],
        },
        transition: {
          duration: 0.50,
          ease: [0.22, 1.18, 0.36, 1],
          times: [0, 0.42, 1],
        },
      };

    case "discard":
      // Card "rolled / flicked" toward pile — gravity dip, no upward jump
      return {
        keyframes: {
          x:       [0, dx * 0.58, dx],
          y:       [0, dy * 0.38 + 14, dy],  // slight gravity pull
          rotate:  [0, dir * 34, dir * 16],   // tumbles in air then settles
          scale:   [1, 0.9, 0.76],
          opacity: [1, 1, 0.60],
        },
        transition: {
          duration: 0.40,
          ease: [0.48, 0.04, 0.76, 0.46],
          times: [0, 0.44, 1],
        },
      };

    default:
      // Generic fly — subtle roll, very small curve
      return {
        keyframes: {
          x:       [0, dx * 0.46, dx],
          y:       [0, dy * 0.40 - 10, dy],
          rotate:  [0, dir * 20, dir * 7],
          scale:   [1, 1.06, 0.86],
          opacity: [1, 1, 0.72],
        },
        transition: {
          duration: 0.62,
          ease: "easeInOut",
          times: [0, 0.44, 1],
        },
      };
  }
}

function FlyingCard({ id, from, to, card, type = "fly", onDone }) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  const { keyframes, transition } = getProfile(type, dx, dy);

  return (
    <motion.div
      key={id}
      className="fixed pointer-events-none"
      style={{
        zIndex: 9999,
        left: from.x - W / 2,
        top: from.y - H / 2,
        width: W,
        height: H,
        willChange: "transform",
        filter:
          type === "discard"
            ? "drop-shadow(0 6px 12px rgba(0,0,0,0.45))"
            : type === "draw"
            ? "drop-shadow(0 2px 6px rgba(0,0,0,0.3))"
            : undefined,
      }}
      initial={{
        x: 0,
        y: 0,
        rotate: keyframes.rotate[0],
        scale: keyframes.scale[0],
        opacity: keyframes.opacity[0],
      }}
      animate={keyframes}
      transition={transition}
      onAnimationComplete={onDone}
    >
      {card
        ? <PlayingCard card={card} size="sm" disabled />
        : <PlayingCard faceDown size="sm" disabled />
      }
    </motion.div>
  );
}

export function CardFlyLayer({ animations }) {
  return (
    <AnimatePresence>
      {animations.map((anim) => (
        <FlyingCard key={anim.id} {...anim} />
      ))}
    </AnimatePresence>
  );
}
